/**
 * The single place that turns a design into the RANS sweep inputs. Used by BOTH the browser
 * (runRans) and the server-side Node CLI (geometry-cli.ts) so the geometry, flow conditions,
 * disk and report metadata are assembled exactly once. Add the per-run alphas/quality/project
 * around it; this part is purely a function of the design.
 */
import { buildConfiguration, type Configuration } from './geometry';
import {
  blowingOf,
  DEFAULT_MAIN_ELEMENT_ID,
  DEFAULT_VANE_ELEMENT_ID,
  DEFAULT_FLAPERON_ELEMENT_ID,
} from './elements';
import { flowConditions } from './flowConditions';
import { gapMetrics, gapMetricOutputs, orderByLeadingEdge } from './gapMetrics';
import type { HighLiftAirfoil, V2 } from './estolConfig';

/** An actuator-disk blowing source on the sweep wire (the RANS/WebGPU disk contract). */
export interface SweepDisk {
  center: V2;
  radius: number;
  thickness: number;
  axis: V2;
  loading: number;
}

export interface SweepInputs {
  elements: { name: string; contour: V2[] }[];
  flow: { reynolds: number; mach: number; temperature: number };
  /** ALL actuator-disk blowing sources for the operating point (FXC-9009 N-disk wire). Empty when
   *  disk loading is 0. The default 1-source config yields a single-element list. */
  disks: SweepDisk[];
  /** @deprecated Back-compat alias for `disks[0] ?? null` — the legacy single-disk wire. New
   *  consumers read `disks`; this stays so older callers (and golden snapshots) don't break. */
  disk: SweepDisk | null;
  operation: Record<string, number>;
  /** deploy/flaperon/spoiler angles, disk loading, hinge pivots (geom) and q∞ — for the polar
   *  reconstruction, mechanism-load statics, and the LLM report. */
  meta: { deployAngle: number; flapAngle: number; spoiler: number; loading: number;
          geom: { deployHinge: V2; flapHinge: V2 }; qinf: number };
  /** Gap-flow / reattachment geometric OUTPUTS for the deployed configuration (FXC-9009 Phase 3).
   *  Pure functions of the deployed contours — slot-prefixed scalars (`slot_mv_*`, `slot_vf_*`)
   *  that Tailor/ADB optimize on. Attached per-sweep (they depend only on the design + geometric
   *  operation knobs, not on α/flow), so the server appends them to every run's value JSON. */
  gapMetrics: Record<string, number>;
  airfoil: string;
}

/**
 * The historical wire name for a deployed element. The default 3-element section emits exactly
 * `main`/`vane`/`flap` (the names the solver + golden snapshots + cache keys have always used), so
 * adding extra elements never perturbs the default's serialization. Any EXTRA element (a 2nd vane,
 * a slat, an added foil) is named by its stable collection id, which is unique and provenance-keyed.
 */
function wireName(id: string): string {
  if (id === DEFAULT_MAIN_ELEMENT_ID) return 'main';
  if (id === DEFAULT_VANE_ELEMENT_ID) return 'vane';
  if (id === DEFAULT_FLAPERON_ELEMENT_ID) return 'flap';
  return id;
}

/**
 * `configuration`, if given, MUST be `buildConfiguration(cfg)` for the same `cfg` — pass it when the
 * caller already has one (e.g. `useFoilDesign`'s per-render `configuration`) to skip rebuilding the
 * full multi-element contour a second time. Every other caller can omit it; the pre-restructure
 * default (build it here) is unchanged, so this is purely additive.
 */
export function buildSweepInputs(cfg: HighLiftAirfoil, configuration?: Configuration): SweepInputs {
  const conf = configuration ?? buildConfiguration(cfg);
  const op = cfg.operation, m = cfg.main;
  const fc = flowConditions(m.chord, op.freestreamKnots, op.densityAltitudeM, op.tempOffsetK);
  const loading = op.diskLoading;

  // Emit EVERY deployed element of the canonical collection (FXC-9009 N-element wire) — main +
  // vane(s) + flaperon(s) + any added foils — not just the first-of-each-kind triple. Degenerate
  // contours (< 3 pts, e.g. a not-yet-built reserved element) are dropped, and the remaining
  // elements are leading-edge sorted so slot_ij outputs match the solver wire. For the default
  // 3-element section this is byte-identical to the historical
  // [main, vane, flap] wire (same names, same order, same contours).
  const deployedElements = orderByLeadingEdge(conf.elements);
  const elements = deployedElements.map((el) => ({ name: wireName(el.id), contour: el.contour }));

  // Emit EVERY blowing source (FXC-9009 N-disk wire), not just `blowing[0]`. Per-source loading is
  // not yet a modeled axis, so all sources share the single operating-point `diskLoading`. With
  // loading 0 (disk off) the list is empty — byte-identical to the legacy `disk: null`. The default
  // 1-source config yields exactly one disk (== the legacy single-disk wire).
  const disks: SweepDisk[] = loading !== 0
    ? blowingOf(cfg).map((d) => {
        const rad = (d.tiltDeg * Math.PI) / 180;
        return {
          center: [d.x, d.z] as V2,
          radius: d.diameter / 2,
          thickness: d.diameter / 16,
          axis: [-Math.cos(rad), -Math.sin(rad)] as V2,
          loading,
        };
      })
    : [];

  return {
    elements,
    flow: { reynolds: fc.reynolds, mach: fc.mach, temperature: fc.temperature },
    disks,
    disk: disks[0] ?? null,
    operation: {
      deployAngleDeg: op.deployAngleDeg, flaperonAngleDeg: op.flaperonAngleDeg,
      spoilerAngleDeg: op.spoilerAngleDeg, chordM: m.chord, freestreamKnots: op.freestreamKnots,
      densityAltitudeM: op.densityAltitudeM, tempOffsetK: op.tempOffsetK,
    },
    meta: {
      deployAngle: op.deployAngleDeg, flapAngle: op.flaperonAngleDeg, spoiler: op.spoilerAngleDeg,
      loading, geom: { deployHinge: cfg.design.deployHinge.pivot, flapHinge: conf.flaperonPivot },
      qinf: 0.5 * fc.density * fc.velocity * fc.velocity,
    },
    // Slot metrics over the FULL deployed collection (N-1 slots), with the legacy slot_mv_/slot_vf_
    // aliases preserved from the fixed triple so existing Tailor axes survive.
    gapMetrics: gapMetricOutputs(
      gapMetrics({
        main: conf.main,
        vane: conf.vane,
        flaperon: conf.flaperon,
        elements: deployedElements.map((el) => ({ id: el.id, contour: el.contour })),
      }),
    ),
    airfoil: 'LS(1)-0417',
  };
}
